import type { Pixel, PixelInfo, Region } from "../types";

/** Raw websocket payload from `on_pixel` (Tornado websocket_handler.py). */
export interface WsPixelLookupContent {
  pixel: number;
  location?: string;
  region?: Region | Region[] | Record<string, unknown>;
  info?: PixelInfo | unknown;
  image?: string;
  user?: string;
  frameName?: string;
  artistName?: string;
  country?: string;
  color?: string;
  status?: number;
}

function normalizeRegion(
  raw: Region | Region[] | Record<string, unknown> | undefined,
): Region | undefined {
  if (!raw) return undefined;
  if (Array.isArray(raw) && raw.length > 0) {
    const first = raw[0] as Region;
    return typeof first === "object" ? first : undefined;
  }
  if (typeof raw === "object") return raw as Region;
  return undefined;
}

/** Google geocode entries: `[[types, formattedAddress], ...]` from websocket `info`. */
function countryFromGeocode(info: unknown): string {
  if (!Array.isArray(info)) return "";
  for (const entry of info) {
    if (!Array.isArray(entry) || entry.length < 2) continue;
    const types = entry[0];
    const label = entry[1];
    if (
      Array.isArray(types) &&
      types.includes("country") &&
      typeof label === "string"
    ) {
      return label;
    }
  }
  return "";
}

/** Build a Pixel for cart/UI from websocket `pixel` lookup response.content. */
export function wsLookupToPixelAndStatus(raw: WsPixelLookupContent): {
  pixel: Pixel;
  status: number;
} {
  const status = typeof raw.status === "number" ? raw.status : 0;

  let info: PixelInfo = { lat: 0, lng: 0 };
  const ri = raw.info;
  if (ri && typeof ri === "object" && "lat" in ri && "lng" in ri) {
    const o = ri as PixelInfo;
    info = {
      lat: Number(o.lat),
      lng: Number(o.lng),
    };
  }

  let image = typeof raw.image === "string" ? raw.image : "";
  if (image && !image.startsWith("http")) {
    image = image.startsWith("/") ? image : `/${image}`;
  }

  const region = normalizeRegion(raw.region);

  const pixel: Pixel = {
    pixel: Number(raw.pixel),
    location:
      typeof raw.location === "string" && raw.location.length ? raw.location : "",
    image: image || undefined,
    frameName: typeof raw.frameName === "string" ? raw.frameName : "",
    artistName: typeof raw.artistName === "string" ? raw.artistName : "",
    country:
      typeof raw.country === "string" && raw.country.length > 0
        ? raw.country
        : countryFromGeocode(raw.info),
    region,
    info,
  };

  return { pixel, status };
}
